import { db } from './firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

/**
 * Get user profile by user ID
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - User profile data
 */
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return {
        userId: userSnap.id,
        ...userSnap.data(),
      };
    } else {
      throw new Error('User not found');
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

/**
 * Get user's complete statistics and idea history
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - User stats and ideas
 */
export const getUserStats = async (userId) => {
  try {
    // Fetch user profile
    const userProfile = await getUserProfile(userId);
    
    // Fetch user's ideas (without orderBy to avoid index requirement)
    const ideasRef = collection(db, 'ideas');
    const q = query(
      ideasRef,
      where('authorId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const ideas = [];
    
    querySnapshot.forEach((doc) => {
      ideas.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    // Sort client-side by createdAt descending
    ideas.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime; // Descending order (newest first)
    });
    
    // Calculate statistics
    const stats = {
      totalIdeas: ideas.length,
      activeIdeas: ideas.filter(idea => idea.status === 'active').length,
      resolvedIdeas: ideas.filter(idea => idea.status === 'resolved').length,
      cancelledIdeas: ideas.filter(idea => idea.status === 'cancelled').length,
      totalVotes: ideas.reduce((sum, idea) => sum + (idea.votes || 0), 0),
      totalViews: ideas.reduce((sum, idea) => sum + (idea.views || 0), 0),
      totalComments: ideas.reduce((sum, idea) => sum + (idea.commentCount || 0), 0),
    };
    
    // Calculate ROI statistics
    // Filter for resolved ideas with ROI data (includes negative ROI values)
    const resolvedIdeasWithROI = ideas.filter(
      idea => idea.status === 'resolved' && idea.roi !== undefined && idea.roi !== null
    );
    
    if (resolvedIdeasWithROI.length > 0) {
      // Calculate total ROI: negative values subtract from the sum
      // Example: [+20%, -15%, +10%] = +15% totalROI
      // Settled markets with negative returns subtract from user's total ROI
      stats.totalROI = resolvedIdeasWithROI.reduce((sum, idea) => sum + idea.roi, 0);
      stats.averageROI = stats.totalROI / resolvedIdeasWithROI.length;
      
      // Find best and worst ideas
      stats.bestIdea = resolvedIdeasWithROI.reduce((best, idea) => 
        !best || idea.roi > best.roi ? idea : best
      , null);
      
      stats.worstIdea = resolvedIdeasWithROI.reduce((worst, idea) => 
        !worst || idea.roi < worst.roi ? idea : worst
      , null);
      
      // Win rate (ideas with positive ROI)
      const winningIdeas = resolvedIdeasWithROI.filter(idea => idea.roi > 0);
      stats.winRate = (winningIdeas.length / resolvedIdeasWithROI.length) * 100;
    } else {
      stats.totalROI = 0;
      stats.averageROI = 0;
      stats.bestIdea = null;
      stats.worstIdea = null;
      stats.winRate = 0;
    }
    
    // Platform breakdown
    const platformCounts = {};
    ideas.forEach(idea => {
      const platform = idea.platform || 'Other';
      platformCounts[platform] = (platformCounts[platform] || 0) + 1;
    });
    stats.platformBreakdown = platformCounts;
    
    // Risk level breakdown
    const riskCounts = {};
    ideas.forEach(idea => {
      const risk = idea.riskLevel || 'Unknown';
      riskCounts[risk] = (riskCounts[risk] || 0) + 1;
    });
    stats.riskBreakdown = riskCounts;
    
    // Calculate average votes and views per idea
    if (ideas.length > 0) {
      stats.avgVotesPerIdea = stats.totalVotes / ideas.length;
      stats.avgViewsPerIdea = stats.totalViews / ideas.length;
    } else {
      stats.avgVotesPerIdea = 0;
      stats.avgViewsPerIdea = 0;
    }
    
    return {
      profile: userProfile,
      stats,
      ideas,
    };
  } catch (error) {
    console.error('Error getting user stats:', error);
    throw error;
  }
};

/**
 * Get user's recent activity
 * @param {string} userId - The user ID
 * @param {number} limit - Number of recent ideas to fetch
 * @returns {Promise<Array>} - Recent ideas
 */
export const getUserRecentActivity = async (userId, limit = 5) => {
  try {
    const ideasRef = collection(db, 'ideas');
    const q = query(
      ideasRef,
      where('authorId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const ideas = [];
    
    querySnapshot.forEach((doc) => {
      ideas.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    // Sort client-side by createdAt descending
    ideas.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
    
    // Return limited results
    return ideas.slice(0, limit);
  } catch (error) {
    console.error('Error getting user recent activity:', error);
    throw error;
  }
};

/**
 * Get user's top performing ideas
 * @param {string} userId - The user ID
 * @param {number} limit - Number of top ideas to fetch
 * @returns {Promise<Array>} - Top performing ideas
 */
export const getUserTopIdeas = async (userId, limit = 5) => {
  try {
    const ideasRef = collection(db, 'ideas');
    const q = query(
      ideasRef,
      where('authorId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const ideas = [];
    
    querySnapshot.forEach((doc) => {
      ideas.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    // Sort by ROI (for resolved ideas) or votes (for active ideas)
    ideas.sort((a, b) => {
      // Prioritize resolved ideas with ROI
      if (a.status === 'resolved' && a.roi !== undefined && a.roi !== null) {
        if (b.status === 'resolved' && b.roi !== undefined && b.roi !== null) {
          return b.roi - a.roi;
        }
        return -1;
      }
      if (b.status === 'resolved' && b.roi !== undefined && b.roi !== null) {
        return 1;
      }
      // Otherwise sort by votes
      return (b.votes || 0) - (a.votes || 0);
    });
    
    return ideas.slice(0, limit);
  } catch (error) {
    console.error('Error getting user top ideas:', error);
    throw error;
  }
};

